/**
 * Per-image cache-busting token protocol, shared by the server (which mints
 * and stamps tokens, see server/utils/imageVersions.ts) and IdunnImageProvider
 * (which reads a stamped token off the source and re-applies it to the Idunn
 * URL it builds) — both must agree on the exact same key and shape, or a
 * purge silently stops reaching the image it was meant to invalidate.
 *
 * Auto-imported from both `app/` and `server/` (Nuxt's `shared/` layer), so
 * this is the ONE definition — no independent copy to drift out of sync.
 */

/**
 * Query-string key carrying the token.
 *
 * Deliberately not the common `v` convention: a source image this provider
 * processes (a banner, a third-party CDN asset) may already carry its own
 * unrelated `?v=` for its own versioning, and mistaking that for this
 * protocol would strip/relocate a value that was never ours to touch.
 */
export const VERSION_QUERY_PARAM = '_pcv';

/** Accepted token shape: opaque, URL/path-segment-safe, bounded length. */
export const VERSION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

/** Max length of a sanitised version token. */
export const VERSION_TOKEN_MAX_LENGTH = 32;

export function isValidVersionToken(token: unknown): token is string {
	return typeof token === 'string' && VERSION_TOKEN_PATTERN.test(token);
}

/**
 * Reduce a version token to characters that are safe inside a URI path
 * segment and match {@link VERSION_TOKEN_PATTERN}.
 *
 * The token can end up in a cosmetic filename segment (path mode), where `/`
 * would create an extra segment and `.` would interfere with Idunn's
 * "last segment containing a dot is a cosmetic filename" rule — either one
 * turns a valid image URL into a 404 rather than a cache miss.
 */
export function sanitizeVersionToken(raw: unknown): string {
	if (typeof raw !== 'string') return '';
	return raw
		.trim()
		.replace(/[^A-Za-z0-9_-]/g, '')
		.slice(0, VERSION_TOKEN_MAX_LENGTH);
}
